import * as React from 'react';
import { App } from '../../../../pkg';
import classname from 'classnames';
import {
  Circle,
  Download,
  Image,
  Keyboard,
  LucideIcon,
  MousePointer2,
  PenTool,
  Pencil,
  Square,
  Triangle,
  Type,
} from 'lucide-react';
import { CursorDrawMode } from '../../../models/cursor';
import BaseToolBar, { BaseToolBarGroupConfig } from '../BaseToolbar';

import './index.scss';

interface IToolbarProps {
  app: App;
  currentMode: CursorDrawMode;
  onChangeMode: (mode: CursorDrawMode) => void;
  onOpenShortcutCheatsheet: () => void;
}

interface ModeAction {
  mode: CursorDrawMode;
  label: string;
  icon: LucideIcon;
  shortcut?: string;
}

interface ExportOptions {
  includeBackground: boolean;
  backgroundColor: string;
  padding: number;
  scale: 1 | 2 | 3;
}

const toolGroups: ModeAction[][] = [
  [{ mode: CursorDrawMode.Cursor, label: 'Select', icon: MousePointer2 }],
  [
    { mode: CursorDrawMode.Vector, label: 'Pen', icon: PenTool, shortcut: 'V' },
    { mode: CursorDrawMode.Pencil, label: 'Pencil', icon: Pencil, shortcut: 'P' },
  ],
  [
    { mode: CursorDrawMode.Rectangle, label: 'Rectangle', icon: Square, shortcut: 'R' },
    { mode: CursorDrawMode.Oval, label: 'Oval', icon: Circle, shortcut: 'O' },
    { mode: CursorDrawMode.Triangle, label: 'Triangle', icon: Triangle, shortcut: 'Y' },
  ],
  [
    { mode: CursorDrawMode.Text, label: 'Text', icon: Type, shortcut: 'T' },
    { mode: CursorDrawMode.Image, label: 'Image', icon: Image, shortcut: 'I' },
  ],
];

const SCALE_OPTIONS: Array<1 | 2 | 3> = [1, 2, 3];
const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  includeBackground: true,
  backgroundColor: '#ffffff',
  padding: 0,
  scale: 1,
};

const Toolbar: React.FC<IToolbarProps> = ({
  app,
  currentMode,
  onChangeMode,
  onOpenShortcutCheatsheet,
}: IToolbarProps) => {
  const [isExportDialogVisible, setIsExportDialogVisible] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportProgress, setExportProgress] = React.useState(0);
  const [exportOptions, setExportOptions] = React.useState<ExportOptions>(DEFAULT_EXPORT_OPTIONS);

  const handleExportImage = React.useCallback(async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    const sanitizedPadding = Number.isFinite(exportOptions.padding)
      ? Math.max(0, exportOptions.padding)
      : 0;
    const sanitizedScale = SCALE_OPTIONS.includes(exportOptions.scale) ? exportOptions.scale : 1;
    const sanitizedColor = /^#[0-9A-Fa-f]{6}$/.test(exportOptions.backgroundColor)
      ? exportOptions.backgroundColor
      : '#ffffff';

    const appWithExports = app as unknown as {
      begin_export_scene_png_with_options?: (
        backgroundEnabled: boolean,
        backgroundColor: string,
        padding: number,
        scale: number,
      ) => number;
      begin_export_scene_png?: () => number;
      export_scene_png_render_step?: (maxTiles: number) => number;
      complete_export_scene_png_canvas?: () => HTMLCanvasElement | null | undefined;
      cancel_export_scene_png?: () => void;
      export_scene_png_canvas_with_options?: (
        backgroundEnabled: boolean,
        backgroundColor: string,
        padding: number,
        scale: number,
      ) => HTMLCanvasElement | null | undefined;
      export_scene_png_canvas?: () => HTMLCanvasElement | null | undefined;
      export_scene_png_with_options?: (
        backgroundEnabled: boolean,
        backgroundColor: string,
        padding: number,
        scale: number,
      ) => string | null | undefined;
      export_scene_png?: () => string | null | undefined;
    };

    const nextFrame = () =>
      new Promise<void>(resolve => {
        window.requestAnimationFrame(() => resolve());
      });

    try {
      setExportProgress(0);
      await nextFrame();

      let downloadUrl: string | null = null;
      let shouldRevokeObjectUrl = false;
      let exportCanvas: HTMLCanvasElement | null = null;

      const canUseIncrementalExport = Boolean(
        appWithExports.begin_export_scene_png_with_options &&
          appWithExports.export_scene_png_render_step &&
          appWithExports.complete_export_scene_png_canvas,
      );

      if (canUseIncrementalExport) {
        const totalTiles =
          appWithExports.begin_export_scene_png_with_options?.(
            exportOptions.includeBackground,
            sanitizedColor,
            sanitizedPadding,
            sanitizedScale,
          ) ?? appWithExports.begin_export_scene_png?.() ?? 0;

        if (totalTiles > 0) {
          const tilesPerFrame = 2;
          let renderedTiles = 0;

          while (renderedTiles < totalTiles) {
            const stepResult = appWithExports.export_scene_png_render_step?.(tilesPerFrame) ?? -1;
            if (stepResult < 0) {
              throw new Error('incremental export failed');
            }
            renderedTiles = Math.min(totalTiles, stepResult);
            setExportProgress(Math.round((renderedTiles / totalTiles) * 100));

            if (renderedTiles < totalTiles) {
              await nextFrame();
            }
          }

          exportCanvas = appWithExports.complete_export_scene_png_canvas?.() ?? null;
          if (!exportCanvas) {
            throw new Error('incremental export completion failed');
          }
        }
      }

      if (!exportCanvas) {
        exportCanvas = appWithExports.export_scene_png_canvas_with_options?.(
          exportOptions.includeBackground,
          sanitizedColor,
          sanitizedPadding,
          sanitizedScale,
        ) ?? appWithExports.export_scene_png_canvas?.() ?? null;
      }

      if (exportCanvas) {
        const canvasForEncode = exportCanvas;
        setExportProgress(100);
        const blob = await new Promise<Blob | null>(resolve => {
          canvasForEncode.toBlob(blobResult => resolve(blobResult), 'image/png');
        });

        if (blob) {
          downloadUrl = window.URL.createObjectURL(blob);
          shouldRevokeObjectUrl = true;
        }
      }

      if (!downloadUrl) {
        downloadUrl = appWithExports.export_scene_png_with_options?.(
          exportOptions.includeBackground,
          sanitizedColor,
          sanitizedPadding,
          sanitizedScale,
        ) ?? appWithExports.export_scene_png?.() ?? null;
      }

      if (!downloadUrl) {
        window.alert('当前没有可导出的图形，或导出失败。');
        return;
      }

      const link = window.document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = downloadUrl;
      link.download = `tylas-export-${timestamp}.png`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();

      if (shouldRevokeObjectUrl) {
        window.URL.revokeObjectURL(downloadUrl);
      }

      setIsExportDialogVisible(false);
      setExportProgress(0);
    } catch {
      appWithExports.cancel_export_scene_png?.();
      window.alert('导出过程中发生错误，请重试。');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  }, [app, exportOptions, isExporting]);

  React.useEffect(() => {
    if (!isExportDialogVisible) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isExporting) {
        setIsExportDialogVisible(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExportDialogVisible, isExporting]);

  return (
    <BaseToolBar
      className="toolbar"
      orientation="vertical"
      ariaLabel="Drawing toolbar"
      groups={[
        ...toolGroups.map((group, idx): BaseToolBarGroupConfig => ({
          key: `group-${idx}`,
          content: (
            <>
              {group.map(tool => {
                const Icon = tool.icon;
                return (
                  <button
                    key={tool.label}
                    type="button"
                    className={classname('tool-bar-button', 'tool-bar-button--icon', {
                      'tool-bar-button--active': currentMode === tool.mode,
                    })}
                    title={tool.label}
                    aria-label={tool.label}
                    onClick={() => {
                      onChangeMode(tool.mode);
                    }}
                  >
                    <Icon />
                    {currentMode === tool.mode ? (
                      <img
                        className="tool-active-brush"
                        src="assets/icons/circle_brush.png"
                        alt=""
                        aria-hidden="true"
                      />
                    ) : null}
                    {tool.shortcut ? <span className="tool-shortcut-indicator">{tool.shortcut}</span> : null}
                  </button>
                );
              })}
            </>
          ),
        })),
        {
          key: 'quick-actions',
          className: 'tool-bar-group--end',
          content: (
            <>
              <button
                type="button"
                className={classname('tool-bar-button', 'tool-bar-button--icon')}
                title="导出图片"
                aria-label="导出图片"
                onClick={() => {
                  setExportOptions(DEFAULT_EXPORT_OPTIONS);
                  setIsExportDialogVisible(true);
                }}
              >
                <Download />
              </button>
              <button
                type="button"
                className={classname('tool-bar-button', 'tool-bar-button--icon')}
                title="快捷键速查"
                aria-label="快捷键速查"
                onClick={onOpenShortcutCheatsheet}
              >
                <Keyboard />
                <span className="tool-shortcut-indicator">?</span>
              </button>
            </>
          ),
        },
      ]}
      groupClassName="tool-bar-group--stack"
      showDividers
    >
      {isExportDialogVisible ? (
        <div
          className="export-dialog-backdrop"
          onClick={() => {
            if (!isExporting) {
              setIsExportDialogVisible(false);
            }
          }}
        >
          <div
            className="export-dialog"
            onClick={event => {
              event.stopPropagation();
            }}
          >
            <div className="export-dialog-title">导出图片</div>
            <label className="export-row export-row-checkbox">
              <input
                type="checkbox"
                checked={exportOptions.includeBackground}
                onChange={event => {
                  setExportOptions(prev => ({
                    ...prev,
                    includeBackground: event.target.checked,
                  }));
                }}
              />
              <span>启用背景色</span>
            </label>
            <label className="export-row">
              <span>背景色</span>
              <input
                type="color"
                value={exportOptions.backgroundColor}
                disabled={!exportOptions.includeBackground}
                onChange={event => {
                  setExportOptions(prev => ({
                    ...prev,
                    backgroundColor: event.target.value,
                  }));
                }}
              />
            </label>
            <label className="export-row">
              <span>Padding</span>
              <input
                type="number"
                min={0}
                step={1}
                value={exportOptions.padding}
                onChange={event => {
                  const nextPadding = Number(event.target.value);
                  setExportOptions(prev => ({
                    ...prev,
                    padding: Number.isFinite(nextPadding) ? nextPadding : 0,
                  }));
                }}
              />
            </label>
            <div className="export-row export-row-scale">
              <span>倍数</span>
              <div className="export-scale-options">
                {SCALE_OPTIONS.map(option => (
                  <label key={option} className="export-scale-option">
                    <input
                      type="radio"
                      name="export-scale"
                      checked={exportOptions.scale === option}
                      onChange={() => {
                        setExportOptions(prev => ({
                          ...prev,
                          scale: option,
                        }));
                      }}
                    />
                    <span>{option}x</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="export-dialog-actions">
              <button
                type="button"
                className="export-btn"
                disabled={isExporting}
                onClick={() => {
                  setIsExportDialogVisible(false);
                }}
              >
                取消
              </button>
              <button
                type="button"
                className="export-btn export-btn-primary"
                disabled={isExporting}
                onClick={handleExportImage}
              >
                {isExporting ? `导出中 ${exportProgress}%` : '导出'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </BaseToolBar>
  );
};

export default Toolbar;
